import { type ResolverTag } from '@ps/web-core-enums';
import { select } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import { useTrackers } from '@ps/web-shared-analytics';
import classNames from 'clsx';
import { isBoolean } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import StarsWizard from '@swf/components/StarsWizard/StarsWizard';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import useWizardLoading from '@swf/core/hooks/useWizardLoading';
import { getDisplayName } from '@swf/core/utils';
import DrukCyTT from '@swf/themes/assets/fonts/Druk-Bold-Cy-Gr-Web.woff';

import Prompts from '../../components/Prompts/Prompts';
import ValidationError from '../../components/ValidationError/ValidationError';
import type { HelpSection } from '../../types/Cashier';
import { mapCashierFlowToPromptTourType } from '../../utils/cashier';
import BonusSummary from '../BonusSummary/BonusSummary';
import SetLimitContent from '../Deposit/components/SetLimitContent/SetLimitContent';
import HelpSupportButton from '../HelpSupportButton/HelpSupportButton';
import PromptTour from '../PromptTour/PromptTour';
import RegulatoryInformation from '../RegulatoryInformation/RegulatoryInformation';

import { TXTWCL_Responsible_gaming } from './Container.messages.json';
import styles from './Container.scss';
import Layout from './Layout';
import Footer from './components/Footer/Footer';
import type { HeaderPropsProp } from './components/Header/Header';
import Page from './components/Page/Page';

interface Props extends HeaderPropsProp {
  formClassName?: string;
  layoutClassName?: string;
  loading?: boolean;
  hideBonusSummary?: boolean;
  page: React.ComponentType;
  pageClassName?: string;
  pageTopSubtitle?: React.ReactNode;
  pageTopTitle?: React.ReactNode;
  showFooter?: boolean;
  helpSupportResolver?: boolean | ResolverTag;
  showRegulatoryInformation?: boolean;
  updateAnalyticsContext?(): void;
  helpSection?: HelpSection;
  limitsSection?: boolean;
}

const Container: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  formClassName,
  layoutClassName,
  loading,
  headerProps,
  hideBonusSummary = false,
  page,
  pageClassName,
  pageTopSubtitle,
  pageTopTitle,
  showFooter,
  helpSupportResolver,
  showRegulatoryInformation = true,
  updateAnalyticsContext,
  helpSection,
  limitsSection
}) => {
  const { loading: serviceLoading } = useWizardLoading();
  const { pathname } = useLocation();
  const snowplowRef = useRef<string>('');
  const { pageView } = useTrackers();
  const isEmbedded = useSelector(select.device.isEmbedded);

  const {
    data: { cmsHeaderAndGridLayout, limitSet, cashierFlow, depositRestricted, signupPassed }
  } = useWizardContext();

  const resolverTagProp = !isBoolean(helpSupportResolver)
    ? { resolverTag: helpSupportResolver as ResolverTag }
    : {};

  const limitSection: HelpSection = {
    title: TXTWCL_Responsible_gaming,
    body: <SetLimitContent limitSet={limitSet} />
  };

  useEffect(() => {
    if (pathname !== '/' && snowplowRef.current !== pathname) {
      snowplowRef.current = pathname;
      updateAnalyticsContext?.();
      pageView(pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (canUseDOM && cmsHeaderAndGridLayout) {
      (document.documentElement as HTMLElement).scrollTop = 0;
    }
  }, [cmsHeaderAndGridLayout]);

  return (
    <>
      <div
        className={classNames(styles.container, {
          [styles.embeddedLayout]: isEmbedded,
          [styles.browserLayout]: !isEmbedded
        })}
      >
        <Helmet>
          {/* eslint-disable-next-line jsx-a11y/html-has-lang */}
          <html className={styles.html} />
          <link rel="preload" href={DrukCyTT} as="font" />
        </Helmet>
        <Layout
          className={classNames(layoutClassName, serviceLoading && styles.loading)}
          headerProps={headerProps}
          showFooter={showFooter}
          applyGridLayout={cmsHeaderAndGridLayout}
          helpSection={helpSection}
          limitsSection={limitsSection ? limitSection : undefined}
        >
          <StarsWizard.Page name={getDisplayName(page)} formClassName={formClassName}>
            <Prompts />
            {!signupPassed && <PromptTour flow={mapCashierFlowToPromptTourType(cashierFlow)} />}
            <ValidationError />
            <Page
              className={pageClassName}
              applyGridLayout={cmsHeaderAndGridLayout}
              loading={loading}
            >
              <div className={styles.contentContainer}>
                <div>
                  {!hideBonusSummary ? <BonusSummary page={getDisplayName(page)} /> : null}
                  {!!pageTopTitle && (
                    <>
                      <h2>{pageTopTitle}</h2>
                      {pageTopSubtitle && <h3>{pageTopSubtitle}</h3>}
                    </>
                  )}
                  {children}
                </div>
                <div>
                  <div className={styles.helpSection}>
                    {!!limitsSection && (
                      <HelpSupportButton limitSet={limitSet || depositRestricted} />
                    )}
                    {!!helpSupportResolver && <HelpSupportButton {...resolverTagProp} />}
                  </div>
                  {showRegulatoryInformation && <RegulatoryInformation />}
                </div>
              </div>
            </Page>
          </StarsWizard.Page>
        </Layout>
      </div>
      <Footer showFooter={showFooter} modal={headerProps.modal} />
    </>
  );
};

export default Container;
